import { Button, Divider, TextField } from '@mui/material'
import { omitBy } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'react-toastify'
import { useAsyncFn } from 'react-use'
import IcBaselineEdit from '~icons/ic/baseline-edit'
import UploadImage from 'src/components/Common/UploadImage'
import { dialogClaim } from 'src/components/GenerationModel/GenerationModelDetail/ModelDetailClaimAction'
import { LoadingIcon } from 'src/components/LoadingPage'
import NotLogin from 'src/components/NotLogin'
import { UIButton } from 'src/components/ui/Button'
import {
  UIDialog,
  UIDialogContent,
  UIDialogHeader,
} from 'src/components/ui/Modal'
import { useMyInfo } from 'src/services/apollo/auth'
import type { Media } from 'src/services/apollo/generated/graphql'
import { RefType, ReportReason } from 'src/services/apollo/generated/graphql'
import { useGetGenerationModel } from 'src/services/apollo/generation-model'
import { executeReport } from 'src/services/apollo/user'
import { useForm } from 'src/utils/useForm'

interface ModelEditRequestForm {
  title?: string
  mediaId?: string
  extra?: {
    description?: string
    triggerWords?: string
  }
}

function ModelEditRequestDialog({
  modelId,
  onComplete,
  previouslyFilledValues,
}: {
  modelId: string
  onComplete: () => void
  previouslyFilledValues?: ModelEditRequestForm
}) {
  const { t } = useTranslation(['market', 'action'])
  const { values, handleChange, setValues } = useForm<ModelEditRequestForm>({
    title: '',
    mediaId: '',
    extra: {
      description: '',
      triggerWords: '',
    },
  })
  const initialValuePopulated = useRef(false)
  const [uploading, setUploading] = useState(false)
  const [uploadedMedia, setUploadedMedia] = useState<Media>()
  const { data: model, loading: loadingModel } = useGetGenerationModel(modelId)
  const { data: userInfo, loading: loadingUserInfo } = useMyInfo()

  const [{ loading: submitting }, submit] = useAsyncFn(async () => {
    try {
      await executeReport({
        refId: modelId,
        refType: RefType.Model,
        reason: ReportReason.RequestEditModel,
        extra: {
          requestEditContent: omitBy(values, el => el === ''),
        },
      })
      toast.success(t('market:model.request-edit.notice.submit-success'))
      onComplete?.()
    } catch (err: any) {
      toast.error(
        t('market:model.request-edit.notice.submit-failed') +
          ', ' +
          err.message,
      )
    }
  }, [values, modelId])

  useEffect(() => {
    if (model && !initialValuePopulated.current) {
      setValues({
        title: model.title,
        mediaId: model.mediaId,
        extra: {
          description: model.extra?.description,
          triggerWords: model.extra?.triggerWords,
        },
      })

      initialValuePopulated.current = true
    }
  }, [model])

  if ((loadingModel && !model) || (loadingUserInfo && !userInfo))
    return (
      <div className="py-12 w-full flex justify-center">
        <LoadingIcon />
      </div>
    )

  if (!userInfo && !loadingUserInfo) return <NotLogin />

  return (
    <>
      <UIDialogHeader
        className="[&>h2]:flex [&>h2]:items-center [&>h2]:gap-2"
        heading={
          <>
            <IcBaselineEdit className="size-6" />{' '}
            {t('market:model.request-edit.request-edit')}
          </>
        }
      ></UIDialogHeader>
      <UIDialogContent>
        {previouslyFilledValues && (
          <div className="text-neutral-800 dark:text-neutral-300 border border-solid border-slate-300 p-4 rounded-lg mb-4">
            <h1 className="font-bold text-lg mb-2">
              {t('market:model.request-edit.previously-filled.title')}
            </h1>
            <p>
              {t('market:model.request-edit.previously-filled.description')}
            </p>
            <div className="mt-2 space-x-2">
              <Button
                component={Link}
                to={`/model/${modelId}`}
                variant="contained"
                onClick={() => onComplete?.()}
              >
                {t('market:submit.file-exists.go')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  dialogClaim(modelId)
                }}
              >
                {t('market:model.claim.claim-this-model')}
              </Button>
            </div>
            <Divider className="my-4" />
            <ul className="space-y-2 mt-2">
              {[
                {
                  key: 'title',
                  label: t('market:submit.model-form.name'),
                  value: previouslyFilledValues.title,
                },
                {
                  key: 'extra.description',
                  label: t('market:model.description.title'),
                  value: previouslyFilledValues.extra?.description,
                },
                {
                  key: 'extra.triggerWords',
                  label: t('market:model.trigger-words'),
                  value: previouslyFilledValues.extra?.triggerWords,
                },
              ]
                .filter(el => !!el.value)
                .map(({ key, label, value }) => (
                  <li key={key} className="flex items-center gap-2">
                    <div>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleChange(key)(value)}
                        disabled={submitting}
                        className="whitespace-nowrap"
                      >
                        {t('market:model.request-edit.previously-filled.use')}
                      </Button>
                    </div>

                    <div>
                      <span className="font-bold">{label}: </span>
                      <span>{value}</span>
                    </div>
                  </li>
                ))}
            </ul>
          </div>
        )}
        <form
          className="flex flex-col"
          onSubmit={e => {
            e.preventDefault()
            submit()
          }}
          action=""
        >
          <div className="flex flex-col md:flex-row gap-4 items-start">
            <div className="flex md:w-1/3 justify-center w-full">
              <UploadImage
                value={uploadedMedia || model?.media}
                onChange={media => {
                  setUploadedMedia(media)
                  setValues(prev => ({ ...prev, mediaId: media?.id }))
                }}
                onUpload={setUploading}
              />
            </div>

            <div className="flex flex-col gap-4 grow w-full">
              <TextField
                label={t('market:submit.model-form.name')}
                value={values.title}
                onChange={handleChange('title')}
              />

              <TextField
                label={t('market:model.description.title')}
                value={values.extra?.description}
                onChange={handleChange('extra.description')}
                multiline
                rows={3}
              />

              <TextField
                label={t('market:model.trigger-words')}
                value={values.extra?.triggerWords}
                onChange={handleChange('extra.triggerWords')}
              />
            </div>
          </div>

          <UIButton
            size="large"
            variant="filled"
            type="submit"
            busy={submitting}
            disabled={uploading}
            className="mx-auto my-4 font-bold self-center"
          >
            <span>{t('market:model.request-edit.submit')}</span>
          </UIButton>
        </form>
      </UIDialogContent>
    </>
  )
}

export const dialogModelRequestEdit = (
  modelId: string,
  previouslyFilledValues?: ModelEditRequestForm,
) => {
  return UIDialog.create({
    dismissable: true,
    children: ({ close }) => (
      <ModelEditRequestDialog
        modelId={modelId}
        previouslyFilledValues={previouslyFilledValues}
        onComplete={() => {
          close()
        }}
      />
    ),
    className: 'max-w-screen-mlg',
  })
}
